import type { StorefrontProduct, StorefrontProductAttribute, StorefrontProductMedia, StorefrontVariant } from "./types";

export function variationLabel(variant: StorefrontVariant): string {
  const options = Object.entries(variant.displayOptionValues ?? variant.optionValues)
    .map(([name, value]) => `${name}: ${value}`).join(", ");
  return variant.title || options || variant.sku || variant.id;
}

/** Every concrete option stays reachable, even in a sparse variation matrix. */
export function variantForAttributeValue(
  variants: readonly StorefrontVariant[], selected: StorefrontVariant | undefined,
  name: string, value: string,
): StorefrontVariant | undefined {
  return variants.filter((variant) => variant.optionValues[name] === value)
    .map((variant, index) => ({ variant, index, matches: Object.entries(selected?.optionValues ?? {})
      .filter(([key, previous]) => key !== name && variant.optionValues[key] === previous).length }))
    .sort((a, b) => b.matches - a.matches || Number(b.variant.inStock) - Number(a.variant.inStock) || a.index - b.index)[0]?.variant;
}

export function variationAttributes(product: StorefrontProduct | null): StorefrontProductAttribute[] {
  if (!product) return [];
  // optionValues comes from PostgreSQL jsonb, which reorders object keys (by
  // length, then bytes). The merchant's declared attribute order is canonical.
  const declaredOrder = product.attributes.map((attribute) => attribute.canonicalName ?? attribute.name);
  const rank = (name: string) => { const index = declaredOrder.indexOf(name); return index === -1 ? Number.MAX_SAFE_INTEGER : index; };
  const names = [...new Set(product.variants.flatMap((variant) => Object.keys(variant.optionValues)))]
    .map((name, index) => ({ name, index }))
    .sort((a, b) => rank(a.name) - rank(b.name) || a.index - b.index)
    .map(({ name }) => name);
  return names.map((name) => {
    const declared = product.attributes.find((attribute) => (attribute.canonicalName ?? attribute.name) === name);
    const actual = [...new Set(product.variants.map((variant) => variant.optionValues[name]).filter(Boolean))];
    return { ...declared, name: declared?.name ?? name, canonicalName: name,
      visible: declared?.visible ?? true, usedForVariations: true,
      values: [...(declared?.values ?? []).filter((value) => actual.includes(value)), ...actual.filter((value) => !declared?.values.includes(value))] };
  });
}

export function productForVariant(product: StorefrontProduct, variant: StorefrontVariant): StorefrontProduct {
  return { ...product, ...variant, id: product.id, variantId: variant.id,
    sku: variant.sku ?? product.sku, currency: variant.currency ?? product.currency, imageUrl: variant.imageUrl ?? product.imageUrl,
    stockQuantity: variant.availableQuantity ?? variant.stockQuantity,
    virtual: variant.virtual || product.virtual, downloadable: variant.downloadable || product.downloadable };
}

export function galleryForVariant(product: StorefrontProduct | null, variantId: string, requireSelection = false): StorefrontProductMedia[] {
  if (!product) return [];
  const images = product.media.filter((item) => item.role !== "download");
  const primaryFirst = (items: StorefrontProductMedia[]) => [...items]
    .sort((a, b) => Number(b.role === "image") - Number(a.role === "image") || a.position - b.position);
  const shared = primaryFirst(images.filter((item) => !item.variantId));
  if (!variantId && requireSelection) return shared;
  const specific = primaryFirst(images.filter((item) => item.variantId === variantId));
  const variantImage = product.variants.find((variant) => variant.id === variantId)?.imageUrl;
  if (variantImage && !specific.some((item) => item.src === variantImage)) {
    specific.unshift({ id: `variant-${variantId}`, variantId, role: "image", position: 0, src: variantImage, altText: product.name });
  }
  const candidates = [...specific, ...shared];
  if (!candidates.length && product.imageUrl) candidates.push({ id: "product-image", role: "image", position: 0, src: product.imageUrl, altText: product.name });
  return candidates.filter((item, index) => candidates.findIndex((other) => other.src === item.src) === index);
}
